import { Image } from "@components";
import { LanguageContext } from "@contexts";
import { cn, getArticleUrl, isPastEvent } from "@lib";
import { Link } from "gatsby";
import React, { useContext } from "react";
import * as styles from "./HappeningThumbnail.module.scss";
import { Heading3 } from "@ui/typography";
import { DateBadge } from "../DateBadge/DateBadge";

interface Content {
  mainImage: {
    asset: object;
  };
  title: {
    [key: string]: string;
  };
  introduction?: {
    nb?: string;
    en?: string;
  };
}

interface Item {
  _type: string;
  slug: {
    current: string;
  };
  content: Content;
  startTime?: string;
  endTime?: string;
  newsTag?: {
    name: string;
  };
}

interface HappeningThumbnailProps {
  item: Item;
  size?: "small" | "medium" | "large";
}

export const HappeningThumbnail: React.FC<HappeningThumbnailProps> = ({
  item,
  size = "medium",
}) => {
  const { _type, content, slug } = item;
  let { mainImage, title, introduction } = content || {};
  const textLength = 55;
  const newsTag = item.newsTag?.name || null;
  const { localize } = useContext(LanguageContext);

  if (introduction) {
    let nb = "";
    let en = "";

    if (introduction.nb) {
      nb =
        introduction.nb.length > textLength
          ? introduction.nb.slice(0, textLength) + "..."
          : introduction.nb;
    }
    if (introduction.en) {
      en =
        introduction.en.length > textLength
          ? introduction.en.slice(0, textLength) + "..."
          : introduction.en;
    }

    introduction = { en, nb };
  } else {
    introduction = {};
  }

  // Update isEventPast to check past events correctly
  const isEventPast = isPastEvent(item.startTime, item.endTime);

  // Check if the event has only a single date (no endTime)
  const isSingleDate = !item.endTime;

  return slug && mainImage && title ? (
    <Link
      to={getArticleUrl(_type, slug.current)}
      className={cn(
        styles.wrapper,
        styles[size],
        isEventPast ? styles.pastEvent : "",
      )}
    >
      <div className={styles.imageWrapper}>
        <DateBadge
          startTime={item.startTime}
          endTime={item.endTime}
          className={styles.badge}
        />
        <Image
          className={styles.image}
          asset={mainImage}
          width={800}
          height={600}
          alt={localize(title)}
        />
      </div>
      <div className={styles.text}>
        <Heading3 uppercase fontWeight="fontWeightLight" fontSize="medium">
          {localize(title)}
        </Heading3>
        <p style={{ marginTop: "1rem" }}>{localize(introduction)}</p>
      </div>
    </Link>
  ) : null;
};
